import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  UserRoundX,
  FileText,
  FilePen,
  XIcon,
} from 'lucide-react';
import { useEffect, useState } from "react";
import { useAuth } from '../../contexts/AuthContext';
import { getRecentAuditLogs } from "../../services/auditService";
import { getDashboardStats } from "../../services/dashboardService";
import { getApprovedMdtsForDashboard } from "../../services/mdtReportService";

const timeAgo = (dateString: string) => {
  const now = new Date().getTime();
  const past = new Date(dateString).getTime();
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

const SuperAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [totalDoctors, setTotalDoctors] = useState(0);
  const [totalMdts, setTotalMdts] = useState(0);
  const [approvedMdts, setApprovedMdts] = useState(0);
  const [rejectedMdts, setRejectedMdts] = useState(0);
  const [submittedMdts, setSubmittedMdts] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  const stats = [
    {
      title: 'Active Doctors',
      value: totalDoctors,
      icon: UserPlus,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Total MDTs',
      value: totalMdts,
      icon: FileText,
      color: 'text-[#5B8FA3]',
      bgColor: 'bg-[#E8F2F5]'
    },
    {
      title: 'Approved MDTs',
      value: approvedMdts,
      icon: FilePen,
      color: 'text-[#15BBAE]',
      bgColor: 'bg-[#E3F7F5]'
    },
    {
      title: 'Rejected MDTs',
      value: rejectedMdts,
      icon: XIcon,
      color: 'text-[#D96F91]',
      bgColor: 'bg-[#FCECEF]'
    }
  ];
  const mapAuditToActivity = (log: any) => {
    const rawTitle = log.title || "";
    const title = rawTitle.toLowerCase();
    const subtitle = [log.actorName, log.targetName].filter(Boolean).join(" • ");
  
    let type:
      | "patient"
      | "report"
      | "doctor-blocked"
      | "doctor-unblocked"
      | "user"
      | "note" = "note";
  
    // ✅ FIX: UNBLOCK MUST COME FIRST
    if (title.includes("doctor") && title.includes("unblock")) {
      type = "doctor-unblocked";
    } 
    else if (title.includes("doctor") && title.includes("blocked")) {
      type = "doctor-blocked";
    } 
    else if (title.includes("patient")) {
      type = "patient";
    } 
    else if (
      title.includes("imaging") ||
      title.includes("histology") ||
      title.includes("clinical") ||
      title.includes("mdt") ||
      title.includes("report")
    ) {
      type = "report";
    } 
    else if (title.includes("doctor") || title.includes("user")) {
      type = "user";
    }
  
    return {
      id: log.id,
      action: rawTitle,
      subtitle,
      createdAt: log.createdAt,
      type,
    };
  };
  
  
  

  /* ================= RECENT ACTIVITY ================= */
  useEffect(() => {
    const loadActivity = async () => {
      try {
        const data = await getRecentAuditLogs();
        const logsArray = Array.isArray(data) ? data : Object.values(data);
        setRecentActivity(logsArray.map(mapAuditToActivity));
      } catch (err) {
        console.error("Failed to load recent audit logs", err);
      }
    };
    loadActivity();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setRecentActivity((prev) => [...prev]);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  /* ================= DASHBOARD STATS ================= */
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await getDashboardStats();
        const stats = res?.data?.stats;

        if (stats) {
          setTotalDoctors(stats.totalDoctors ?? 0);
          setTotalMdts(stats.totalMdts ?? 0);
          setApprovedMdts(stats.approvedMdts ?? 0);
          setRejectedMdts(stats.rejectedMdts ?? 0);
        }
      } catch (err) {
        console.error("Failed to load dashboard stats", err);
      }
    };
    loadStats();
  }, []);

  /* ================= APPROVED MDTs ================= */
  useEffect(() => {
    const loadApprovedMdts = async () => {
      try {
        const data = await getApprovedMdtsForDashboard();
        setSubmittedMdts(data);
      } catch (err) {
        console.error("Failed to load approved MDTs", err);
      }
    };
    loadApprovedMdts();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          System Administration Dashboard
        </h1>
        <p className="text-gray-600">
          Welcome back, {user?.name || 'System Administrator'}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* RECENT ACTIVITY */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <button
              onClick={() => navigate("/activity")}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
            </button>
          </div>

          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.type === "patient"
                      ? "bg-primary-100"
                      : activity.type === "report"
                      ? "bg-purple-100"
                      : activity.type === "doctor-unblocked"
                      ? "bg-green-100"
                      : activity.type === "doctor-blocked"
                      ? "bg-red-100"
                      : "bg-gray-100"
                  }`}
                >
                  {activity.type === "patient" && (
                    <Users className="h-4 w-4 text-primary-600" />
                  )}
                  {activity.type === "report" && (
                    <FileText className="h-4 w-4 text-purple-600" />
                  )}
                  {activity.type === "doctor-unblocked" && (
                    <UserPlus className="h-4 w-4 text-green-600" />
                  )}
                  {activity.type === "doctor-blocked" && (
                    <UserRoundX className="h-4 w-4 text-red-600" />
                  )}
                  {activity.type === "user" && (
                    <UserPlus className="h-4 w-4 text-gray-600" />
                  )}
                </div>

                <div className="flex-1">
                  <p className="font-medium text-gray-900">{activity.action}</p>
                  <p className="text-sm text-gray-600">{activity.subtitle}</p>
                  <p className="text-xs text-gray-500">
                    {timeAgo(activity.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MDT SUBMITTED */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">MDT Submitted</h3>
            <button
              onClick={() => navigate("/reports")}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View all
            </button>
          </div>

          <div className="space-y-3">
            {submittedMdts.length === 0 && (
              <p className="text-sm text-gray-500">No MDT submissions pending</p>
            )}

            {submittedMdts.map((mdt) => (
              <div
                key={mdt.id}
                className="p-3 rounded-lg border-l-4 bg-primary-50 border-primary-400"
              >
                <p className="text-sm font-medium text-gray-900">{mdt.title}</p>
                <p className="text-xs text-gray-600 mt-1">
                  Submitted by <strong>{mdt.submittedBy}</strong>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {timeAgo(mdt.createdAt)}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SuperAdminDashboard;
